import React, { useState } from 'react';
import { XIcon, HistoryIcon, CheckIcon, TrendingUpIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { DraggableModal } from './ui/DraggableModal';
import { formatCurrency } from '../utils/currencyFormat';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
interface PreviousCoding {
  glCode: string;
  description: string;
  lastUsed: string;
  frequency: number;
  totalAmount: number;
  currency: string;
}
interface PreviousCodingModalProps {
  vendor: string;
  lineItemCount: number;
  onApply: (glCode: string) => void;
  onClose: () => void;
}
// Mock data - in real app, this would come from API based on vendor history
const getMockPreviousCodings = (vendor: string): PreviousCoding[] => {
  // Different codings based on vendor
  if (vendor.includes('Dell') || vendor.includes('Tech')) {
    return [{
      glCode: '6200-1100-0000',
      description: 'IT Equipment & Hardware',
      lastUsed: '15/01/2024',
      frequency: 12,
      totalAmount: 245000,
      currency: 'AUD'
    }, {
      glCode: '6200-1200-0000',
      description: 'IT Software & Licenses',
      lastUsed: '08/01/2024',
      frequency: 8,
      totalAmount: 89000,
      currency: 'AUD'
    }, {
      glCode: '6200-1300-0000',
      description: 'IT Support & Maintenance',
      lastUsed: '22/12/2023',
      frequency: 5,
      totalAmount: 45000,
      currency: 'AUD'
    }];
  } else if (vendor.includes('Office') || vendor.includes('Supplies')) {
    return [{
      glCode: '6400-1300-0000',
      description: 'Office Supplies & Stationery',
      lastUsed: '20/01/2024',
      frequency: 24,
      totalAmount: 67000,
      currency: 'AUD'
    }, {
      glCode: '6400-1400-0000',
      description: 'Office Equipment',
      lastUsed: '12/01/2024',
      frequency: 6,
      totalAmount: 23000,
      currency: 'AUD'
    }];
  } else if (vendor.includes('Furniture')) {
    return [{
      glCode: '6300-1200-0000',
      description: 'Furniture & Fixtures',
      lastUsed: '18/01/2024',
      frequency: 8,
      totalAmount: 156000,
      currency: 'AUD'
    }, {
      glCode: '6300-1100-0000',
      description: 'Office Fit-out',
      lastUsed: '05/12/2023',
      frequency: 3,
      totalAmount: 89000,
      currency: 'AUD'
    }];
  }
  // Default generic codings
  return [{
    glCode: '6500-1000-0000',
    description: 'General Expenses',
    lastUsed: '10/01/2024',
    frequency: 5,
    totalAmount: 34000,
    currency: 'AUD'
  }];
};
export function PreviousCodingModal({
  vendor,
  lineItemCount,
  onApply,
  onClose
}: PreviousCodingModalProps) {
  const previousCodings = getMockPreviousCodings(vendor);
  const [selectedGLCode, setSelectedGLCode] = useState<string | null>(null);
  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [{
      key: 'Escape',
      handler: onClose,
      description: 'Close modal'
    }],
    enabled: true
  });
  const handleApply = () => {
    if (selectedGLCode) {
      onApply(selectedGLCode);
    }
  };
  return <DraggableModal isOpen={true} onClose={onClose} title={`Previous GL Coding for ${vendor}`} defaultWidth={900} defaultHeight={600} minWidth={700} minHeight={400} data-id="element-1858">
      <div className="flex flex-col h-full" data-id="element-1859">
        {/* Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-1860">
          <div className="flex items-center gap-2" data-id="element-1861">
            <HistoryIcon className="w-5 h-5 text-purple-600" data-id="element-1862" />
            <p className="text-sm text-gray-600" data-id="element-1863">
              Select a GL code from previous invoices to apply to all{' '}
              {lineItemCount} line items
            </p>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1864">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-1865" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1866">
          {previousCodings.length === 0 ? <div className="text-center py-12" data-id="element-1867">
              <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1868" />
              <p className="text-gray-600" data-id="element-1869">
                No previous GL coding found for {vendor}
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1870">
                This vendor hasn't been coded before
              </p>
            </div> : <div className="space-y-3" data-id="element-1871">
              <div className="text-sm text-gray-600 mb-4" data-id="element-1872">
                Found {previousCodings.length} previous GL code
                {previousCodings.length !== 1 ? 's' : ''} for this vendor
              </div>

              {previousCodings.map(coding => {
            const isSelected = selectedGLCode === coding.glCode;
            return <button key={coding.glCode} onClick={() => setSelectedGLCode(coding.glCode)} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/50'}`} data-id="element-1873">
                    <div className="flex items-start justify-between" data-id="element-1874">
                      <div className="flex-1" data-id="element-1875">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1876">
                          <code className={`text-base font-mono font-bold px-3 py-1.5 rounded ${isSelected ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-900'}`} data-id="element-1877">
                            {coding.glCode}
                          </code>
                          {isSelected && <CheckIcon className="w-5 h-5 text-purple-600" data-id="element-1878" />}
                        </div>
                        <div className="text-sm font-medium text-gray-900 mb-3" data-id="element-1879">
                          {coding.description}
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-1880">
                          <div data-id="element-1881">
                            <div className="text-gray-500 mb-1" data-id="element-1882">Last Used</div>
                            <div className="font-semibold text-gray-900" data-id="element-1883">
                              {coding.lastUsed}
                            </div>
                          </div>
                          <div data-id="element-1884">
                            <div className="text-gray-500 mb-1" data-id="element-1885">Times Used</div>
                            <div className="font-semibold text-gray-900 flex items-center gap-1" data-id="element-1886">
                              <TrendingUpIcon className="w-3 h-3 text-green-600" data-id="element-1887" />
                              {coding.frequency}x
                            </div>
                          </div>
                          <div data-id="element-1888">
                            <div className="text-gray-500 mb-1" data-id="element-1889">
                              Total Amount
                            </div>
                            <div className="font-semibold text-gray-900" data-id="element-1890">
                              {formatCurrency(coding.totalAmount, coding.currency, true)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>;
          })}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0" data-id="element-1891">
          <div className="text-sm text-gray-600" data-id="element-1892">
            {selectedGLCode ? <span className="text-purple-700 font-medium" data-id="element-1893">
                ✓ Ready to apply{' '}
                <code className="font-mono font-bold" data-id="element-1894">{selectedGLCode}</code> to{' '}
                {lineItemCount} line items
              </span> : <span data-id="element-1895">Select a GL code to continue</span>}
          </div>
          <div className="flex gap-3" data-id="element-1896">
            <Button variant="primary" onClick={handleApply} disabled={!selectedGLCode} data-id="element-1897">
              Apply to All {lineItemCount} Lines
            </Button>
            <Button variant="ghost" onClick={onClose} data-id="element-1898">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </DraggableModal>;
}